import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Button, Heading } from '@diwata/ironvale'
import { DiwaAPI } from '@/api/client'
import { SignalsAPI } from '@/api/signals'
import { useUI } from '@/store/ui'
import { useLedgerSelection } from '@/store/ledgerSelection'
import { HelpHint } from '@/components/HelpHint'
import { FlowDiagram } from '@/components/FlowDiagram'
import './ledger.css'

// The Ledger — the searchable table of records you HAVE. Your springs collect rows
// into a shared pool; this page lets you search, sort, filter and re-lens them, and
// keep the ones that matter by tracking a row (it becomes a star you hold). Which
// tables exist, their filters and their columns all come from the registry as data,
// so a brand-new domain shows up here with zero new code — nothing on this page is
// specific to any one kind of record.

interface FilterSpec {
  key: string
  label: string
  type: 'text' | 'flag'
}

interface ColumnSpec {
  key: string
  label: string
  format?: 'money' | 'date'
}

interface DatasetSpec {
  domain: string
  key: string
  label: string
  filters: FilterSpec[]
  columns: ColumnSpec[]
  trackable: boolean
  views?: {
    kanban?: { group_by: string }
    calendar?: { date_field: string }
    map?: { place_field: string }
  }
}

type ViewKey = 'list' | 'kanban' | 'calendar' | 'map'

// The lenses, in plain words. "kanban" is an implementation name, not a user word —
// a person groups records into a "Board". Map keys never leak to the UI.
const VIEW_LABEL: Record<ViewKey, string> = {
  list: 'Table',
  kanban: 'Board',
  calendar: 'Calendar',
  map: 'Map',
}

type Row = Record<string, unknown>

/** A location field can arrive as a plain string or as a nested object (some sources
 *  ship a country/state shape). Pull a readable label out of whatever the row carries;
 *  never invent one — an unrecognised shape reads as "Unspecified". */
function placeLabel(v: unknown): string {
  if (v == null) return 'Unspecified'
  if (typeof v === 'string') return v || 'Unspecified'
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>
    const state = o['state']
    if (typeof state === 'string' && state) return state
    if (state && typeof state === 'object') {
      const code = (state as Record<string, unknown>)['code'] ?? (state as Record<string, unknown>)['name']
      if (typeof code === 'string' && code) return code
    }
    const country = o['country']
    if (country && typeof country === 'object') {
      const code = (country as Record<string, unknown>)['code']
      if (typeof code === 'string' && code && code !== 'USA') return code
    }
  }
  return 'Unspecified'
}

function rowTitle(r: Row, cols: ColumnSpec[]): string {
  const first = cols[0] ? r[cols[0].key] : null
  return String(first ?? '—')
}

// A stable row key that isn't tied to any one domain's id column. Try the common id
// shapes a source might carry, then fall back to the page index.
const ID_KEYS = ['id', '_id', 'uid', 'uuid', 'external_id']
function rowKey(r: Row, i: number): string {
  for (const k of ID_KEYS) {
    const v = r[k]
    if (v != null && v !== '') return String(v)
  }
  return String(i)
}

// The provenance affordance: every row keeps the page it came from. Read the source
// link off whatever field the row actually carries — we never fabricate one, so a row
// with no source simply shows no link.
const SOURCE_KEYS = ['source_url', 'url', 'link', 'permalink', 'href']
function sourceUrl(r: Row): string | null {
  for (const k of SOURCE_KEYS) {
    const v = r[k]
    if (typeof v === 'string' && /^https?:\/\//i.test(v)) return v
  }
  return null
}

const PAGE = 50

function fmt(v: unknown, format?: string): string {
  if (v == null || v === '') return '—'
  if (format === 'money') return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  return String(v)
}

/** A column key → its human label, for copy that names a field (calendar month, etc.). */
function fieldLabel(cols: ColumnSpec[], key: string): string {
  return cols.find((c) => c.key === key)?.label ?? key.replace(/_/g, ' ')
}

type TrackState = 'idle' | 'busy' | 'done' | 'error'

export function Ledger() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const signalsApi = useMemo(() => new SignalsAPI(diwaUrl), [diwaUrl])
  const select = useLedgerSelection((st) => st.select)
  const selected = useLedgerSelection((st) => st.selected)

  const { data: reg } = useQuery({
    queryKey: ['ledger-datasets'],
    queryFn: () => api.get<{ datasets: DatasetSpec[] }>('/ledger/datasets'),
  })
  const datasets = reg?.datasets ?? []
  const multiDomain = new Set(datasets.map((d) => d.domain)).size > 1

  const [active, setActive] = useState<string | null>(null)
  const spec = datasets.find((d) => `${d.domain}/${d.key}` === active) ?? datasets[0] ?? null

  const [filters, setFilters] = useState<Record<string, string>>({})
  const [page, setPage] = useState(0)
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null)
  const [view, setView] = useState<ViewKey>('list')
  const [tracked, setTracked] = useState<Record<string, TrackState>>({})
  const [month, setMonth] = useState(() => {
    const d = new Date()
    return { y: d.getFullYear(), m: d.getMonth() }
  })

  const { data, isLoading, isError } = useQuery({
    enabled: spec != null,
    queryKey: ['ledger', spec?.domain, spec?.key, filters, page],
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(PAGE), offset: String(page * PAGE) })
      for (const f of spec!.filters) {
        const v = (filters[f.key] ?? '').trim()
        if (v) params.set(f.key, v)
      }
      return api.get<{ rows: Record<string, unknown>[]; total: number }>(
        `/ledger/${spec!.domain}/${spec!.key}?${params}`,
      )
    },
  })

  const rows = useMemo(() => {
    const rs = [...(data?.rows ?? [])]
    if (sort) {
      rs.sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key]
        if (av == null) return 1
        if (bv == null) return -1
        return (av > bv ? 1 : av < bv ? -1 : 0) * sort.dir
      })
    }
    return rs
  }, [data, sort])

  const { data: stats } = useQuery({
    enabled: spec != null,
    queryKey: ['ledger-stats', spec?.domain, spec?.key],
    queryFn: () => api.get<{ total: number; active: number | null; promoted: number }>(
      `/ledger/${spec!.domain}/${spec!.key}/stats`,
    ),
  })

  // The spine, made visible: a pool row your domain rules PROMOTED becomes a Signal that
  // carries the same source_url. Fetch this domain's signals and index their source pages,
  // so a row that already crossed into Signals wears a quiet "◈ signal" marker. Pure
  // frontend cross-reference — the row and the signal are the same fact, joined by origin.
  const { data: domainSignals } = useQuery({
    enabled: spec != null,
    queryKey: ['signals'],
    queryFn: () => signalsApi.list(),
  })
  const promotedUrls = useMemo(() => {
    const set = new Set<string>()
    for (const s of domainSignals ?? []) {
      if (s.domain === spec?.domain && s.source_url) set.add(s.source_url)
    }
    return set
  }, [domainSignals, spec?.domain])

  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE))
  const cols = spec?.columns ?? []

  function switchTo(d: DatasetSpec) {
    setActive(`${d.domain}/${d.key}`)
    setFilters({})
    setPage(0)
    setSort(null)
    setView('list')
    select(null)
  }

  async function track(r: Row, k: string, e: React.MouseEvent) {
    e.stopPropagation()
    if (!spec) return
    setTracked((st) => ({ ...st, [k]: 'busy' }))
    try {
      await api.post('/ledger/track', { domain: spec.domain, dataset: spec.key, row: r })
      setTracked((st) => ({ ...st, [k]: 'done' }))
    } catch {
      setTracked((st) => ({ ...st, [k]: 'error' }))
    }
  }

  const availableViews: ViewKey[] = ['list']
  if (spec?.views?.kanban) availableViews.push('kanban')
  if (spec?.views?.calendar) availableViews.push('calendar')
  if (spec?.views?.map) availableViews.push('map')

  return (
    <div>
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">
            Ledger <HelpHint gloss="The shared pool of records your springs collect — search, sort and filter them, and track a row to keep it as a star you hold." /> <FlowDiagram />
          </div>
          <div className="dw-page-subtitle">
            {spec
              ? `${total.toLocaleString()} ${spec.label.toLowerCase()} collected — search, filter, and track the ones worth keeping.`
              : 'Everything your springs have collected, in one searchable table.'}
          </div>
        </div>
        <div className="dw-tabs" style={{ marginBottom: 0 }}>
          {datasets.map((d) => (
            <button
              key={`${d.domain}/${d.key}`}
              className={`dw-tab${spec === d ? ' dw-tab--active' : ''}`}
              onClick={() => switchTo(d)}
            >
              {multiDomain ? `${d.domain} · ${d.label}` : d.label}
            </button>
          ))}
        </div>
      </div>

      {reg && datasets.length === 0 && (
        <div className="dw-empty">
          No tables here yet — the Ledger shows only the domains you actually have.
          Attach a spring on the <a className="dw-link" href="/springs">Springs</a> page, or
          add a domain, and its records appear here.
        </div>
      )}

      {spec && stats && (
        <div className="dw-inbox-detail" style={{ marginBottom: 8 }}>
          {stats.total.toLocaleString()} records
          {stats.active != null && ` · ${stats.active.toLocaleString()} active`}
          {' · '}
          {stats.promoted > 0
            ? `${stats.promoted.toLocaleString()} tracked`
            : 'none tracked yet — star a row to keep it'}
        </div>
      )}

      {spec && availableViews.length > 1 && (
        <div className="dw-lens-row">
          <Heading level={4} as="span">View</Heading>
          <div className="dw-tabs" style={{ marginBottom: 0 }}>
            {availableViews.map((v) => (
              <button
                key={v}
                className={`dw-tab${view === v ? ' dw-tab--active' : ''}`}
                aria-pressed={view === v}
                onClick={() => setView(v)}
              >
                {VIEW_LABEL[v]}
              </button>
            ))}
          </div>
        </div>
      )}

      {spec && spec.filters.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {spec.filters.map((f) =>
            f.type === 'flag' ? (
              <label key={f.key} className="dw-inbox-detail" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={filters[f.key] === 'true'}
                  onChange={(e) => { setFilters((st) => ({ ...st, [f.key]: e.target.checked ? 'true' : '' })); setPage(0) }}
                />
                {f.label}
              </label>
            ) : (
              <input
                key={f.key}
                className="dw-input"
                style={{ width: 170 }}
                placeholder={`Filter by ${f.label.toLowerCase()}…`}
                value={filters[f.key] ?? ''}
                onChange={(e) => { setFilters((st) => ({ ...st, [f.key]: e.target.value })); setPage(0) }}
              />
            ),
          )}
        </div>
      )}

      {isLoading && <p className="dw-empty">Loading records…</p>}
      {isError && <p className="dw-error">Couldn't load records — try again shortly.</p>}
      {data && rows.length === 0 && <div className="dw-rail-empty">Nothing matches these filters.</div>}

      {spec && view === 'kanban' && spec.views?.kanban && rows.length > 0 && (
        <div className="dw-ledger-scroll">
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            {[...rows.reduce((acc, r) => {
              const k = String(r[spec.views!.kanban!.group_by] ?? '') || 'None'
              acc.set(k, [...(acc.get(k) ?? []), r])
              return acc
            }, new Map<string, Row[]>())].sort((a, b) => b[1].length - a[1].length).map(([group, groupRows]) => (
              <div key={group} style={{ minWidth: 230, flex: '0 0 auto' }}>
                <Heading level={3} className="dw-ledger-group">
                  <span>{group}</span>
                  <span className="dw-ledger-group__count">{groupRows.length}</span>
                </Heading>
                <div style={{ display: 'grid', gap: 6 }}>
                  {groupRows.map((r, i) => (
                    <button key={i} type="button" className="dw-queue-row"
                            style={{ outline: selected?.row === r ? '1px solid var(--ae-color-accent)' : 'none' }}
                            onClick={() => select({ domain: spec.domain, dataset: spec.key, row: r })}>
                      <span className="dw-queue-title">
                        {rowTitle(r, cols).slice(0, 60)}
                        <span className="dw-inbox-detail">{String(r[cols[1]?.key] ?? '')}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {spec && view === 'calendar' && spec.views?.calendar && (() => {
        const field = spec.views.calendar.date_field
        const byDay = new Map<string, Row[]>()
        for (const r of rows) {
          const raw = String(r[field] ?? '').slice(0, 10)
          if (raw) byDay.set(raw, [...(byDay.get(raw) ?? []), r])
        }
        const first = new Date(month.y, month.m, 1)
        const label = first.toLocaleString(undefined, { month: 'long', year: 'numeric' })
        const lead = first.getDay()
        const days = new Date(month.y, month.m + 1, 0).getDate()
        const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)]
        return (
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <Button size="sm" variant="ghost" onClick={() => setMonth(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }))}>←</Button>
              <Heading level={3} as="span" style={{ margin: 0 }}>{label} <span className="dw-ledger-group__count">by {fieldLabel(cols, field).toLowerCase()}</span></Heading>
              <Button size="sm" variant="ghost" onClick={() => setMonth(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }))}>→</Button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
              {cells.map((day, i) => {
                if (day == null) return <div key={`x${i}`} />
                const iso = `${month.y}-${String(month.m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                const dayRows = byDay.get(iso) ?? []
                return (
                  <div key={iso} style={{ minHeight: 64, border: '1px solid var(--ae-color-border, rgba(128,128,128,.25))', borderRadius: 6, padding: 4 }}>
                    <div className="dw-inbox-detail">{day}</div>
                    {dayRows.slice(0, 3).map((r, j) => (
                      <button key={j} type="button" className="dw-link"
                              style={{ display: 'block', background: 'none', border: 'none', padding: 0, fontSize: 'calc(11px * var(--ae-text-scale, 1))', textAlign: 'left', cursor: 'pointer' }}
                              onClick={() => select({ domain: spec.domain, dataset: spec.key, row: r })}>
                        {rowTitle(r, cols).slice(0, 22)}
                      </button>
                    ))}
                    {dayRows.length > 3 && <div className="dw-inbox-detail">+{dayRows.length - 3} more</div>}
                  </div>
                )
              })}
            </div>
            {byDay.size === 0 && <div className="dw-rail-empty" style={{ marginTop: 8 }}>No dated rows on this page — try Next or widen filters.</div>}
          </div>
        )
      })()}

      {spec && view === 'map' && spec.views?.map && rows.length > 0 && (
        <div>
          <div className="dw-inbox-detail" style={{ marginBottom: 8 }}>
            Grouped by location for this page. A drawn map comes later.
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {[...rows.reduce((acc, r) => {
              const k = placeLabel(r[spec.views!.map!.place_field])
              acc.set(k, [...(acc.get(k) ?? []), r])
              return acc
            }, new Map<string, Row[]>())].sort((a, b) => b[1].length - a[1].length).map(([place, placeRows]) => (
              <div key={place}>
                <Heading level={3} className="dw-ledger-group">
                  <span>{place}</span>
                  <span className="dw-ledger-group__count">{placeRows.length}</span>
                </Heading>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {placeRows.map((r, i) => (
                    <button key={i} type="button" className="dw-chip" style={{ cursor: 'pointer' }}
                            onClick={() => select({ domain: spec.domain, dataset: spec.key, row: r })}>
                      {rowTitle(r, cols).slice(0, 40)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {spec && view === 'list' && rows.length > 0 && (
        <div className="dw-ledger-scroll">
          <table className="dw-ledger">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key} onClick={() =>
                    setSort((s) => (s?.key === c.key ? { key: c.key, dir: s.dir === 1 ? -1 : 1 } : { key: c.key, dir: 1 }))}>
                    {c.label}{sort?.key === c.key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
                <th aria-label="Row actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const isSel = selected?.row === r
                const k = rowKey(r, i)
                const src = sourceUrl(r)
                const tState = tracked[k]
                return (
                  <tr key={k}
                      className={isSel ? 'dw-ledger-row--selected' : ''}
                      onClick={() => select({ domain: spec.domain, dataset: spec.key, row: r })}>
                    {cols.map((c) => (
                      <td key={c.key} className={c.format === 'money' ? 'dw-ledger-money' : ''}>
                        {fmt(r[c.key], c.format)}
                      </td>
                    ))}
                    <td className="dw-ledger-actions">
                      {src && promotedUrls.has(src) && (
                        <Link
                          className="dw-row-action dw-signal-marker"
                          to="/signals"
                          title="Your rules promoted this row into a signal — open it in Signals"
                          onClick={(e) => e.stopPropagation()}
                        >
                          ◈ signal
                        </Link>
                      )}
                      {spec.trackable && (
                        <button
                          type="button"
                          className={`dw-row-action${tState === 'done' ? ' dw-row-action--on' : ''}`}
                          disabled={tState === 'busy' || tState === 'done'}
                          title={tState === 'done' ? 'Tracked — kept as a star' : 'Track this row — keep it as a star you hold'}
                          onClick={(e) => track(r, k, e)}
                        >
                          {tState === 'busy' ? '…' : tState === 'done' ? '✦ Tracked' : tState === 'error' ? '☆ Retry' : '☆ Track'}
                        </button>
                      )}
                      {src && (
                        <a
                          className="dw-row-action dw-row-source"
                          href={src}
                          target="_blank"
                          rel="noreferrer"
                          title="Open the original page this record came from"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View source ↗
                        </a>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
          <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Prev</Button>
          <span className="dw-inbox-detail">page {page + 1} of {pages.toLocaleString()}</span>
          <Button size="sm" variant="ghost" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next →</Button>
        </div>
      )}
    </div>
  )
}
